import { useState } from 'react';
import { Spinner } from '../components/ui';
import { useAuth } from '../lib/auth';

// Split sign-in shell, same pattern as WnW Drive: brand panel on the left, form
// panel on the right. The brand panel hides on narrow screens.
export function LoginPage() {
  const { signIn } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(username.trim(), password);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell">
      <aside className="auth-brand">
        <img src="/brand/wnw-logo-white-vertical.svg" alt="Wheels and Wonder" className="auth-logo" />
        <div className="auth-hero">
          <BookletIllustration />
          <h1>Every tour. <span>Every page.</span></h1>
          <p>Print-ready tour booklets for Wheels and Wonder — from the itinerary doc and price sheet to a PDF your riders can hold.</p>
        </div>
        <span className="auth-foot">Wheels &amp; Wonder</span>
      </aside>

      <main className="auth-main">
        <div className="auth-column">
          <form className="auth-card" onSubmit={(e) => void submit(e)}>
            <img src="/brand/wnw-logo-dark-green-vertical.svg" alt="Wheels and Wonder" className="auth-card-logo" />
            <div className="auth-title">Booklet Generator</div>
            <div className="auth-desc">Sign in to your booklet workspace</div>

            <label htmlFor="username">Username</label>
            <input id="username" autoFocus autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} />

            <label htmlFor="password">Password</label>
            <div className="auth-password">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="button" onClick={() => setShowPassword((s) => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>

            {error && <p className="auth-error" role="alert">{error}</p>}

            <button className="auth-submit" disabled={busy || !username || !password}>
              {busy && <Spinner />} Sign in
            </button>
            <p className="auth-note">No account yet? Ask an admin to add you.</p>
          </form>
          <p className="auth-copy">&copy; {new Date().getFullYear()} Wheels &amp; Wonder</p>
        </div>
      </main>
    </div>
  );
}

// Line-art counterpart to WnW Drive's camera scene: an open booklet with a route
// drawn across it, a map pin and a faint globe. Lime lines, gold accents.
function BookletIllustration() {
  const lime = '#d7e026';
  const gold = '#c9a14a';
  const faint = 'rgba(255,255,255,0.16)';
  return (
    <svg viewBox="0 0 480 340" fill="none" aria-hidden="true" className="auth-illustration">
      <defs>
        <clipPath id="booklet-globe-clip"><circle cx="372" cy="92" r="62" /></clipPath>
      </defs>
      <g stroke={faint} strokeWidth="1.5">
        <circle cx="372" cy="92" r="62" />
        <g clipPath="url(#booklet-globe-clip)">
          <ellipse cx="372" cy="92" rx="24" ry="62" />
          <ellipse cx="372" cy="92" rx="46" ry="62" />
          <line x1="310" y1="92" x2="434" y2="92" />
          <path d="M 314 66 Q 372 82 430 66" />
          <path d="M 314 118 Q 372 102 430 118" />
        </g>
      </g>
      <g stroke={gold} strokeWidth="2" strokeLinecap="round">
        <path d="M 318 46 L 318 58 M 312 52 L 324 52" />
        <path d="M 440 164 L 440 174 M 435 169 L 445 169" />
      </g>

      <g stroke={lime} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        {/* open booklet */}
        <path d="M 240 118 Q 180 96 96 106 L 96 286 Q 180 276 240 298 Z" />
        <path d="M 240 118 Q 300 96 384 106 L 384 286 Q 300 276 240 298" />
        <path d="M 240 118 L 240 298" />
        {/* left page: photo stamp + text lines */}
        <rect x="118" y="134" width="96" height="64" rx="4" strokeDasharray="4 5" />
        <path d="M 118 184 L 144 162 L 164 178 L 182 166 L 214 190" />
        <path d="M 120 220 L 212 224 M 120 238 L 196 241 M 120 256 L 204 259" />
        {/* right page: route with stops */}
        <path d="M 270 250 C 290 200, 320 240, 336 196 S 350 150, 360 140" strokeDasharray="1 9" strokeWidth="3.5" />
      </g>
      <g fill={lime}>
        <circle cx="270" cy="250" r="5" />
        <circle cx="336" cy="196" r="5" />
      </g>
      <g stroke={lime} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 360 140 C 360 122, 346 112, 360 96 C 374 112, 360 122, 360 140 Z" transform="translate(0 -6)" />
        <circle cx="360" cy="108" r="5" />
      </g>

      <path d="M 56 318 L 424 318" stroke={faint} strokeWidth="2" strokeLinecap="round" strokeDasharray="2 14" />
      <g fill={gold}>
        <circle cx="70" cy="200" r="3" />
        <circle cx="250" cy="70" r="3" />
        <circle cx="440" cy="236" r="3" />
      </g>
    </svg>
  );
}

const Eye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9.9 4.24A9 9 0 0 1 12 4c7 0 10 8 10 8a13 13 0 0 1-1.67 2.68" />
    <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3 7 10 7a9.7 9.7 0 0 0 5.39-1.61" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" /><line x1="2" y1="2" x2="22" y2="22" />
  </svg>
);
